import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseCsvText, csvRowsToEditableRows } from './csvParse';
import { isCsvFile } from '../shared/csvFileValidation';
import { readCsvFile } from '../shared/readCsvFile';
import type { FundamentalDto } from '@/api/recognition';
import type { BulkEditableRow } from './types';

interface Props {
  fundamentals: FundamentalDto[];
  onParsed: (rows: BulkEditableRow[]) => void;
  onBack: () => void;
}

/** Paso 2 del wizard: selección/drag-and-drop del CSV y parseo local. */
export function StepUploadFile({ fundamentals, onParsed, onBack }: Readonly<Props>) {
  const { t } = useTranslation('recognition');
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFile = async (file: File) => {
    setParseError(null);
    if (!isCsvFile(file)) {
      setParseError(t('bulk.invalidFileType'));
      return;
    }
    try {
      const text = await readCsvFile(file);
      const parsed = parseCsvText(text);
      if (parsed.length === 0) { setParseError(t('bulk.emptyFile')); return; }
      setFileName(file.name);
      onParsed(csvRowsToEditableRows(parsed, fundamentals));
    } catch (error) {
      if (import.meta.env.DEV) console.error('No se pudo parsear el CSV de carga masiva:', error);
      setParseError(t('bulk.parseError'));
    }
  };

  return (
    <div className="bulk-step bulk-step--upload">
      <button
        type="button"
        className={`bulk-dropzone ${isDragOver ? 'bulk-dropzone--over' : ''}`}
        onClick={() => fileRef.current?.click()}
        onDragOver={(event) => { event.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setIsDragOver(false);
          const file = event.dataTransfer.files?.[0];
          if (file) void handleFile(file);
        }}
      >
        <i className="fas fa-file-csv" />
        <p>{t('bulk.dragDropHint')}</p>
        {fileName && <span className="bulk-filename">{fileName}</span>}
      </button>
      <input
        ref={fileRef}
        data-testid="bulk-file-input"
        type="file"
        accept=".csv,text/csv"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void handleFile(file);
          event.target.value = '';
        }}
      />

      {parseError && <p className="rp-error" role="alert"><i className="fas fa-triangle-exclamation" /> {parseError}</p>}

      <div className="bulk-wizard-nav">
        <button type="button" className="btn btn--secondary" onClick={onBack}>
          <i className="fas fa-arrow-left" /> {t('bulk.back')}
        </button>
        <button type="button" className="btn btn--secondary" onClick={() => fileRef.current?.click()}>
          <i className="fas fa-upload" /> {t('bulk.upload')}
        </button>
      </div>
    </div>
  );
}

export default StepUploadFile;
